/**
 * Input with per-action buffering.
 *
 * Buffering lives here rather than in the movement controller because
 * *everything* wants it — jump, dash, attacks, ability activation. A press is
 * recorded with a timestamp; consumers ask "was this pressed within N ms and not
 * yet consumed?" and consume it. That single primitive is what makes the whole
 * game feel responsive rather than just fast.
 */

export type Action =
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'jump'
  | 'dash'
  | 'grip'
  | 'mainAttack'
  | 'offAttack'
  | 'ability'
  | 'parry'
  | 'interact'
  | 'swap'
  | 'inventory'
  | 'codex'
  | 'map'
  | 'pause';

/**
 * The shipped key map.
 *
 * Exported because rebinding is a *diff* against it and nothing else: to move
 * jump from Space to Z you have to know which codes currently produce jump, and
 * the only place that knows is this table. Keeping it private is what made key
 * rebinding impossible to offer honestly — see `unbind` below.
 */
export const DEFAULT_BINDINGS: Record<string, Action> = {
  KeyA: 'left',
  ArrowLeft: 'left',
  KeyD: 'right',
  ArrowRight: 'right',
  KeyW: 'up',
  ArrowUp: 'up',
  KeyS: 'down',
  ArrowDown: 'down',
  Space: 'jump',
  ShiftLeft: 'dash',
  ShiftRight: 'dash',
  // Cling is a *held* grip, not a state you fall into: see docs/02 §2.5 — the
  // material wall-slide has to stay reachable for a player who owns Cling.
  KeyF: 'grip',
  KeyJ: 'mainAttack',
  KeyK: 'offAttack',
  KeyL: 'ability',
  // Parry is a distinct verb from attack (docs/04 §4): the gauntlet's and the
  // shield's whole identity is parry-into-counter, and it had no binding.
  KeyI: 'parry',
  KeyE: 'interact',
  KeyQ: 'swap',
  Tab: 'inventory',
  KeyC: 'codex',
  KeyM: 'map',
  Escape: 'pause',
};

const MOUSE_BINDINGS: Record<number, Action> = {
  0: 'mainAttack',
  2: 'offAttack',
  1: 'ability',
};

/**
 * The shipped pad map: `Gamepad.buttons` index -> Action, in the W3C **standard
 * mapping** every browser normalises a recognised controller into.
 *
 * Exported for the same reason `DEFAULT_BINDINGS` is: a table nobody can read
 * is a table nobody can check. `tests/gamepad.test.ts` drives every entry
 * through `update()` and asserts the action comes out, which is the strongest
 * available form of "the poll actually consults this" — this project's
 * recurring defect is an authored field nothing reads, and a pad map that the
 * poll had drifted away from would look exactly like a working feature.
 *
 * Sixteen buttons, seventeen actions: one cannot fit. `codex` is the one left
 * off, and `PAD_UNBOUND` in the test records that as a decision with a reason
 * rather than as an oversight — two-way, so binding it later fails there.
 *
 * Changed from the original map, deliberately:
 *   - 6 and 7 were **both** `dash`, which the old poll's unconditional
 *     `else release(...)` turned into a dash that lasted zero frames — button 7
 *     being up cancelled button 6 being down, every frame. 6 is now `parry`,
 *     which had no pad binding at all despite being a distinct verb the
 *     gauntlet and shield are built around (docs/04 §4).
 *   - 8 (`inventory`) and 11 (`map`) were unbound.
 *   - 12–15, the d-pad, were unbound — which meant a pad without a usable
 *     stick could not move the character at all. They map to the same four
 *     actions WASD does, so they arrive at `axis` through the keyboard path in
 *     `update()` and need no separate handling.
 *
 * ---------------------------------------------------------------------------
 * THE STICK: continuous for movement, quantised for aim. This is the decision.
 * ---------------------------------------------------------------------------
 *
 * A stick is a continuous direction and the game ships *eight*-directional
 * aiming. The reconciliation is:
 *
 *   - `axis` stays continuous. `entity/movement.ts` reads `axis.x` as an
 *     analogue magnitude, so quantising at the source would throw away the
 *     walk/run gradient that is a pad's whole advantage over WASD.
 *   - The **aim** quantises, through the existing `aimAngleFor` and no second
 *     code path. Its 0.5 component threshold turns any stick direction into one
 *     of five values — `NaN`, ±π/4, ±π/2 — in three equal 30° sectors per
 *     quadrant.
 *
 * Quantising is the conservative choice and it is chosen, not inherited:
 * `WielderState.aim` is consumed by a skeleton poser that *enumerates* those
 * five values (`entity/pixelBodySystem.ts`) and by weapon nodes authored
 * against them. A continuous aim would not be a finer number in the same slot,
 * it would invalidate the enumeration. Wanting continuous aim is a change to
 * those consumers first, and to this file second.
 *
 * The **right stick is unbound**, for the reason the `aimAngleFor` comment
 * gives: only the vertical half of the aim comes from the stick, and the
 * horizontal half is `facing`, which the movement controller owns. A right
 * stick that steered only the vertical would be half a feature; one that
 * steered both needs facing decoupled from movement, which is not a change to
 * the input layer.
 */
export const DEFAULT_PAD_BINDINGS: Readonly<Record<number, Action>> = {
  0: 'jump', // A / cross
  1: 'offAttack', // B / circle
  2: 'mainAttack', // X / square
  3: 'interact', // Y / triangle
  4: 'swap', // LB / L1
  5: 'ability', // RB / R1
  6: 'parry', // LT / L2
  7: 'dash', // RT / R2
  8: 'inventory', // Back / Share
  9: 'pause', // Start / Options
  10: 'grip', // L3 — a hold, like the keyboard's F
  11: 'map', // R3
  12: 'up',
  13: 'down',
  14: 'left',
  15: 'right',
};

/**
 * The W3C standard mapping's conventional short name for each button index —
 * issue #193. `settingsModel.ts`'s `padButtonLabel` prints "Button N" for the
 * rebinding row, where the number *is* the information (the row exists so a
 * player can tell two buttons apart before they have learned the pad's
 * layout). An on-screen hint is the opposite case — the player already knows
 * where A is — so it gets the face-value name instead, off the same table
 * `DEFAULT_PAD_BINDINGS`' comments and `MENU_PAD_BUTTONS`' already spell out
 * for every index this project binds anything to.
 */
const PAD_GLYPH: Readonly<Record<number, string>> = {
  0: 'A',
  1: 'B',
  2: 'X',
  3: 'Y',
  4: 'LB',
  5: 'RB',
  6: 'LT',
  7: 'RT',
  8: 'Back',
  9: 'Start',
  10: 'L3',
  11: 'R3',
  12: '↑',
  13: '↓',
  14: '←',
  15: '→',
};

/** The short name for a button index, or 'unbound' for null — the hint twin of `padButtonLabel`. */
export function padGlyph(button: number | null): string {
  return button === null ? 'unbound' : (PAD_GLYPH[button] ?? `Button ${button}`);
}

/**
 * Stick deflection below which the left stick reads as centred, and its
 * default — issue #192.
 *
 * Exported so the test can assert behaviour on both sides of it rather than
 * transcribing 0.22 a second time, and so `settingsModel.ts` can seed the
 * settings row's default from the same number the poll starts at. It used to
 * be a bare constant with a comment explaining why it was deliberately *not*
 * a setting — the integrator that pushes a chosen value into the live
 * `Input` did not exist. `setPadDeadzone`/`getPadDeadzone` below are that
 * integrator; `main.ts`'s `applyGlobalSettings` is what calls it.
 */
export const PAD_DEADZONE = 0.22;

/**
 * Clamp bounds for the deadzone setting.
 *
 * `MIN` stops a value near 0 from turning centre-rest stick noise into
 * phantom movement — a dead-cheap failure mode on worn analogue sticks.
 * `MAX` stops a value near 1 from making the stick require a full pin to
 * register at all, which is the "unusable" `#192` asks to be clamped away
 * from. Applied in `setPadDeadzone` itself, not just at the settings row, so
 * a hand-edited save file cannot hand the poll a value neither end of the UI
 * would ever offer.
 */
export const MIN_PAD_DEADZONE = 0.05;
export const MAX_PAD_DEADZONE = 0.6;

/**
 * The deadzone the poll actually uses. Module-scoped rather than a field on
 * `Input`, because the setting has to reach `MenuPad`'s stick quantiser too
 * (`ui/menuPad.ts`'s `stickDirection`), and `MenuPad` instances exist on
 * screens — the title, run setup — where no `Input` has been constructed yet.
 * One shared value is the same shape `applyGlobalSettings` already uses for
 * `--ui-fs`: a user preference applied globally, not a property of one `Game`.
 */
let padDeadzone = PAD_DEADZONE;

/** The deadzone `pollGamepad` and `stickDirection` are currently using. */
export function getPadDeadzone(): number {
  return padDeadzone;
}

/**
 * The settings row's apply path — issue #192. Clamped to
 * `[MIN_PAD_DEADZONE, MAX_PAD_DEADZONE]` so a setting nothing bounds cannot
 * hand the stick a value that reads as broken in either direction.
 */
export function setPadDeadzone(v: number): void {
  padDeadzone = Math.min(MAX_PAD_DEADZONE, Math.max(MIN_PAD_DEADZONE, v));
}

/**
 * Triggers that report an analogue `value` without ever setting `pressed`.
 *
 * Some pads (and some browsers' HID fallbacks) leave `GamepadButton.pressed`
 * false on the analogue triggers and only move `value`. Reading both is what
 * makes buttons 6 and 7 work on those, and it costs nothing on the pads that
 * set `pressed` correctly.
 */
const PAD_TRIGGER_THRESHOLD = 0.5;

/**
 * The source token a pad button's hold is registered under.
 *
 * Action state records *who* is holding an action, not just *that* it is held,
 * and a pad button is a source in exactly the sense a key is. That one
 * mechanism fixes four bugs at once: the pad poll releasing an action the
 * keyboard was holding, a dropped pad releasing one the keyboard was holding,
 * two pad buttons bound to the same action cancelling each other, and — on the
 * keyboard's own side — releasing ArrowLeft cancelling the `left` that KeyA was
 * still producing. Deliberately the same path for all four: a pad-only
 * aggregation would have been a second mechanism doing the same job, tested
 * half as well.
 */
const padSource = (button: number): string => `pad:${button}`;

/**
 * Stick deflection past which an axis counts as *held* for aiming.
 *
 * The same 0.5 the dash's 8-way quantiser uses (`entity/movement.ts`), so the
 * direction a dash goes and the direction a swing goes are read off the stick
 * by the same rule. A player who has learned one has learned the other.
 *
 * **Deliberately independent of the `padDeadzone` setting — issue #192.** The
 * two solve different problems: `padDeadzone` is a noise floor on a
 * *continuous* magnitude, sized to swallow centre-rest jitter while still
 * reaching 1.0 at full deflection. `AIM_DEADZONE` is a component threshold
 * that decides which of three sectors a component falls in (see
 * `aimAngleFor`), and 0.5 is what puts the sector boundaries at a clean 30°
 * and 60° — a geometry fact, not a comfort setting, and the sweep in
 * `tests/gamepad.test.ts` pins it. Scaling it off `padDeadzone` would move
 * those boundaries every time a player retuned stick comfort, for a control
 * that has nothing to do with aim. If continuous aim sensitivity is ever
 * wanted, it is a second setting against this constant, not a multiplier on
 * the movement one.
 */
const AIM_DEADZONE = 0.5;

/**
 * The eight directions a weapon can be pointed, as an angle in the wielder's
 * **local** frame — the frame every hitbox is authored in, where +x is forward
 * (whichever way the character happens to face) and +y is *down*, matching the
 * world's screen-space convention.
 *
 * Only the *vertical* is read from the stick. The horizontal half of the aim is
 * `facing`, which A/D and the stick already steer, and that is the entire
 * aim-versus-movement rule: aiming borrows no key from walking, so a player can
 * never be holding a modifier that stops them moving, and the swing can never
 * disagree with the sprite. The cost is that you cannot walk left while firing
 * right — which is the honest trade, because the alternative is a dedicated aim
 * modifier and there is no free key next to WASD (F, Q, E, C, I, J, K, L, Tab
 * and M are all bound) to spend on it.
 *
 * Returns `NaN` for **neutral** — no vertical held. Neutral is not "aim
 * forward": it means the node keeps the angle its author gave it, so every
 * weapon in the catalogue behaves exactly as it did before aiming existed.
 * That distinction is load-bearing; see `wielder.ts`'s `fireOrSwing`.
 *
 * **This is also the pad's quantiser, deliberately.** A stick is a continuous
 * direction, and it arrives here as one — `axis` is never rounded, because
 * movement reads it as an analogue magnitude. The thresholds above are what
 * turn it into eight, in three equal 30° sectors per quadrant (|sin θ| > 0.5
 * above 30°, |cos θ| > 0.5 below 60°). There is no separate pad aim path and
 * the reasons there is not are recorded at `DEFAULT_PAD_BINDINGS`; the sector
 * geometry is pinned by a sweep in `tests/gamepad.test.ts`, so retuning
 * `AIM_DEADZONE` has to be argued for rather than noticed later.
 */
export function aimAngleFor(axisX: number, axisY: number): number {
  const vy = Math.abs(axisY) > AIM_DEADZONE ? Math.sign(axisY) : 0;
  if (vy === 0) return NaN;
  // `atan2(±1, 1)` is ±π/4 and `atan2(±1, 0)` is ±π/2: the four diagonals and
  // the two verticals. Combined with the two signs of `facing`, that is eight.
  return Math.atan2(vy, Math.abs(axisX) > AIM_DEADZONE ? 1 : 0);
}

interface ActionState {
  down: boolean;
  /** Game-clock ms of the most recent press, -Infinity if never. */
  pressedAt: number;
  releasedAt: number;
  /** Set when a consumer claims the buffered press. */
  consumed: boolean;
  /** Held duration in seconds, for charge attacks and variable jump. */
  heldFor: number;
  /**
   * The input sources currently holding this action: `'k:KeyA'`, `'m:0'`,
   * `'pad'`.
   *
   * `down` used to be a bare boolean, and everything that could hold an action
   * from two places at once was broken by it. Two keys produce `left` (A and
   * the arrow), two buttons produce `mainAttack` (the mouse and the pad's X),
   * and releasing *either* released the action while the other was still down.
   * The action is down while this set is non-empty, and no sooner or later.
   */
  sources: Set<string>;
}

export class Input {
  private state = new Map<Action, ActionState>();
  private bindings: Record<string, Action> = { ...DEFAULT_BINDINGS };
  /** See `bindingSerial`. Every write to `bindings` goes through rebind/unbind. */
  private bindSerial = 0;
  /**
   * The pad table this `Input` is actually polling — issue #191. Starts as a
   * copy of `DEFAULT_PAD_BINDINGS` for the same reason `bindings` starts as a
   * copy of `DEFAULT_BINDINGS`: a per-instance table that `rebindPad`/`unbindPad`
   * can mutate without touching the shipped constant every other `Input` (and
   * `dropPadButtons`, before this existed) still read directly.
   */
  private padBindings: Record<number, Action> = { ...DEFAULT_PAD_BINDINGS };
  /** See `bindingSerial`, the pad twin. */
  private padBindSerial = 0;
  private el: HTMLElement;

  /** Pointer position in CSS pixels relative to the canvas. */
  readonly pointer = { x: 0, y: 0, inside: false };
  /** Pointer position in world units — written by the camera each frame. */
  readonly aim = { x: 0, y: 0 };

  /** Gamepad support: index of the active pad, or -1. */
  private padIndex = -1;
  /**
   * The pad button indices held on the *previous* poll.
   *
   * The whole of the pad's edge detection. Pressing on entry and releasing on
   * exit is what lets a disabled `Input` swallow a press without also
   * swallowing the release that follows it — the old code re-pressed on every
   * frame, so nothing downstream could tell a hold from a hammered button.
   */
  private padButtons = new Set<number>();
  /** Analog axes, already deadzoned. Keyboard writes ±1 into these too. */
  readonly axis = { x: 0, y: 0 };

  private listeners: Array<[EventTarget, string, EventListener]> = [];
  private enabled = true;

  /**
   * Monotonic *game* time in ms, advanced by the simulation rather than by the
   * wall.
   *
   * docs/02 §4 requires that hitstop not consume buffered input. On a wall
   * clock it always does: a 90 ms hitstop plus 60 ms of flight is 150 ms of
   * real time against a 116 ms jump buffer, so the jump the player entered
   * during the freeze is silently dropped. Frame hitches and the debugger eat
   * buffers the same way. Advance this from the same place that steps the
   * movement controller and time stops for the buffers exactly when it stops
   * for the character.
   */
  private clock = 0;

  constructor(el: HTMLElement) {
    this.el = el;
    for (const a of new Set(Object.values(DEFAULT_BINDINGS))) {
      this.state.set(a, {
        down: false,
        pressedAt: -Infinity,
        releasedAt: -Infinity,
        consumed: true,
        heldFor: 0,
        sources: new Set(),
      });
    }
    this.attach();
  }

  private on<K extends keyof WindowEventMap>(
    target: EventTarget,
    type: K | string,
    fn: (e: any) => void,
    opts?: AddEventListenerOptions,
  ): void {
    target.addEventListener(type, fn as EventListener, opts);
    this.listeners.push([target, type, fn as EventListener]);
  }

  private attach(): void {
    this.on(window, 'keydown', (e: KeyboardEvent) => {
      if (!this.enabled) return;
      const action = this.bindings[e.code];
      if (!action) return;
      // Tab and Space would otherwise scroll or move focus out of the canvas.
      if (e.code === 'Tab' || e.code === 'Space' || e.code.startsWith('Arrow')) {
        e.preventDefault();
      }
      if (e.repeat) return;
      this.press(action, `k:${e.code}`);
    });

    this.on(window, 'keyup', (e: KeyboardEvent) => {
      const action = this.bindings[e.code];
      if (action) this.release(action, `k:${e.code}`);
    });

    this.on(this.el, 'mousedown', (e: MouseEvent) => {
      if (!this.enabled) return;
      const action = MOUSE_BINDINGS[e.button];
      if (action) {
        e.preventDefault();
        this.press(action, `m:${e.button}`);
      }
    });

    this.on(window, 'mouseup', (e: MouseEvent) => {
      const action = MOUSE_BINDINGS[e.button];
      if (action) this.release(action, `m:${e.button}`);
    });

    this.on(this.el, 'contextmenu', (e: Event) => e.preventDefault());

    this.on(window, 'mousemove', (e: MouseEvent) => {
      const r = this.el.getBoundingClientRect();
      this.pointer.x = e.clientX - r.left;
      this.pointer.y = e.clientY - r.top;
      this.pointer.inside =
        this.pointer.x >= 0 &&
        this.pointer.y >= 0 &&
        this.pointer.x < r.width &&
        this.pointer.y < r.height;
    });

    // Releasing everything on blur prevents the classic "held key stuck after
    // alt-tab" bug, which in a platformer reads as the game being broken.
    this.on(window, 'blur', () => this.releaseAll());
    this.on(document, 'visibilitychange', () => {
      if (document.hidden) this.releaseAll();
    });

    // These two are a convenience, not the mechanism. `gamepadconnected` is
    // *not* guaranteed to arrive before the first button press, and a pad
    // plugged in before this tab existed never fires one at all — so discovery
    // has to happen in the poll, which is the only thing that runs every frame.
    // Honouring the event as well just means a pad is live one frame earlier.
    this.on(window, 'gamepadconnected', (e: GamepadEvent) => {
      // First pad wins. Adopting the newest would let a second controller
      // joining a couch session take the first player's inputs away mid-run.
      if (this.padIndex < 0) this.padIndex = e.gamepad.index;
    });
    this.on(window, 'gamepaddisconnected', (e: GamepadEvent) => {
      if (this.padIndex !== e.gamepad.index) return;
      this.padIndex = -1;
      // Releasing here rather than waiting for the next poll is the difference
      // between "the cable came out" and "attack is held down for the rest of
      // the run", which is the failure users actually hit.
      this.dropPadButtons();
    });
  }

  private press(a: Action, source: string): void {
    const s = this.state.get(a)!;
    s.sources.add(source);
    if (s.down) return;
    s.down = true;
    s.pressedAt = this.clock;
    s.consumed = false;
    s.heldFor = 0;
  }

  private release(a: Action, source: string): void {
    const s = this.state.get(a);
    if (!s) return;
    s.sources.delete(source);
    // Still held by somebody else — the mouse, the other arrow key, the pad.
    if (!s.down || s.sources.size > 0) return;
    s.down = false;
    s.releasedAt = this.clock;
  }

  /**
   * Let go of everything, whoever was holding it.
   *
   * `padActions` is deliberately *not* cleared: it is the edge memory, and
   * emptying it would make a button still physically held look like a fresh
   * press on the very next poll — undoing the release this method just
   * performed. Leaving it means a pad button held through a blur or through an
   * open menu has to be released and pressed again, which is exactly what a key
   * held through a blur already does.
   */
  releaseAll(): void {
    for (const s of this.state.values()) {
      s.sources.clear();
      if (!s.down) continue;
      s.down = false;
      s.releasedAt = this.clock;
    }
  }

  /** Fall-edge every action the pad is holding, and forget it was holding them. */
  private dropPadButtons(): void {
    for (const btn of this.padButtons) this.release(this.padBindings[btn], padSource(btn));
    this.padButtons.clear();
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.releaseAll();
  }

  /**
   * Advance the buffering clock by one *simulation* step.
   *
   * Deliberately separate from `update()`: `update` runs every rendered frame,
   * including frames the simulation is frozen for. Call this only where the
   * movement controller actually steps, so a freeze cannot age a buffer.
   */
  advanceClock(dt: number): void {
    this.clock += dt * 1000;
  }

  /** Game-clock ms. Buffer windows are measured against this, not the wall. */
  get nowMs(): number {
    return this.clock;
  }

  /** Call once per frame, before any systems read input. */
  update(dt: number): void {
    const padDeflected = this.pollGamepad();

    for (const s of this.state.values()) {
      if (s.down) s.heldFor += dt;
    }

    // Keyboard is authoritative unless a stick is actually deflected this
    // frame. The tempting form — "only overwrite if the axis is near zero" —
    // leaves a stale analog value latched forever when no pad is present,
    // which reads in-game as the character sprinting into a wall by itself.
    const kx = (this.held('right') ? 1 : 0) - (this.held('left') ? 1 : 0);
    const ky = (this.held('down') ? 1 : 0) - (this.held('up') ? 1 : 0);
    if (!padDeflected || kx !== 0) this.axis.x = kx;
    if (!padDeflected || ky !== 0) this.axis.y = ky;
  }

  /**
   * The pad the poll is currently reading, discovering or re-discovering one as
   * needed, or `null`.
   *
   * Scanning here rather than trusting `gamepadconnected` is the whole of the
   * mid-run appear/disappear handling. The API is poll-based: `getGamepads()`
   * hands back a sparse array whose slots go null when a pad leaves, and on
   * several platforms the disconnect event is the thing that does not arrive.
   * A slot that has emptied therefore has to be treated as a disconnect on its
   * own, and a slot that has filled as a connect.
   */
  private activePad(): Gamepad | null {
    // `navigator` is absent under Node and `getGamepads` under older embeddings;
    // neither is an error, it just means there is no pad.
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') {
      return null;
    }
    const pads = navigator.getGamepads();
    if (this.padIndex >= 0) {
      const held = pads[this.padIndex];
      if (held && held.connected !== false) return held;
      this.padIndex = -1;
    }
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (!p || p.connected === false) continue;
      this.padIndex = typeof p.index === 'number' ? p.index : i;
      return p;
    }
    return null;
  }

  /** Returns true if a connected stick is deflected past the deadzone. */
  private pollGamepad(): boolean {
    const wasIndex = this.padIndex;
    const pad = this.activePad();
    // The pad we were reading is gone — unplugged, or swapped for another one.
    // Either way its buttons have to fall, or they stay latched forever.
    if (this.padIndex !== wasIndex) this.dropPadButtons();
    if (!pad) return false;

    // The buttons the pad reports held *this* frame. Edges are taken against
    // the previous frame's set rather than by re-pressing every frame, which is
    // what the old loop did.
    const want = new Set<number>();
    for (const key in this.padBindings) {
      const btn = Number(key);
      const b = pad.buttons[btn];
      if (!b) continue;
      if (b.pressed || (b.value ?? 0) > PAD_TRIGGER_THRESHOLD) want.add(btn);
    }

    for (const btn of want) {
      // A press swallowed while disabled must not fire later: the button is
      // still recorded in `want` below, so it reads as already-held once input
      // comes back and only a real release-and-press produces an edge. Closing
      // a menu with a thumb resting on A does not jump.
      if (!this.padButtons.has(btn) && this.enabled) {
        this.press(this.padBindings[btn], padSource(btn));
      }
    }
    for (const btn of this.padButtons) {
      if (!want.has(btn)) this.release(this.padBindings[btn], padSource(btn));
    }
    this.padButtons = want;

    if (!this.enabled) return false;

    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    // Radial deadzone with rescale, so the usable range still reaches 1.0.
    // Radial rather than per-axis because a square deadzone leaves a stick held
    // straight up registering a sideways value that never reaches zero, which
    // reads in-game as a character who drifts while aiming.
    const mag = Math.hypot(ax, ay);
    if (!(mag > padDeadzone)) return false;
    // Clamped: real pads overshoot to ~1.41 on the diagonals, and an axis past
    // 1 arrives downstream as a speed multiplier nothing was tuned for.
    const scaled = Math.min(1, (mag - padDeadzone) / (1 - padDeadzone));
    this.axis.x = (ax / mag) * scaled;
    this.axis.y = (ay / mag) * scaled;
    return true;
  }

  // ------------------------------------------------------------ queries

  /** Is the action currently held? */
  held(a: Action): boolean {
    return this.state.get(a)?.down ?? false;
  }

  /** Seconds the action has been held (0 if not held). */
  heldFor(a: Action): number {
    const s = this.state.get(a);
    return s && s.down ? s.heldFor : 0;
  }

  /** Was it pressed this frame (unconsumed, within one frame)? */
  pressed(a: Action): boolean {
    return this.buffered(a, 34);
  }

  /**
   * Was the action pressed within `windowMs` and not yet consumed?
   * Does NOT consume — use `consume()` for that. Separating the two lets a
   * system peek (for animation intent) without stealing the input.
   */
  buffered(a: Action, windowMs: number): boolean {
    const s = this.state.get(a);
    if (!s || s.consumed) return false;
    return this.clock - s.pressedAt <= windowMs;
  }

  /** Consume a buffered press. Returns true if there was one to consume. */
  consume(a: Action, windowMs: number): boolean {
    const s = this.state.get(a);
    if (!s || s.consumed) return false;
    if (this.clock - s.pressedAt > windowMs) return false;
    s.consumed = true;
    return true;
  }

  /** Milliseconds since release; Infinity if never released or still held. */
  sinceRelease(a: Action): number {
    const s = this.state.get(a);
    if (!s || s.down) return Infinity;
    return this.clock - s.releasedAt;
  }

  /** Clear a buffered press without acting on it (e.g. on state transition). */
  clearBuffer(a: Action): void {
    const s = this.state.get(a);
    if (s) s.consumed = true;
  }

  /**
   * Forget that `source` is holding anything.
   *
   * Rebinding a key that happens to be down would otherwise strand its token:
   * the keyup that follows is looked up against the *new* table, so it releases
   * some other action and the old one stays held with a source nothing will
   * ever remove. That is a latch, and latches are what this file's source-set
   * bookkeeping exists to prevent.
   */
  private forgetSource(source: string): void {
    for (const s of this.state.values()) {
      if (!s.sources.delete(source)) continue;
      if (!s.down || s.sources.size > 0) continue;
      s.down = false;
      s.releasedAt = this.clock;
    }
  }

  /**
   * The table the game is actually being played on — #134.
   *
   * The HUD has to print key caps, and until this existed the only table it
   * could reach was `DEFAULT_BINDINGS`, so a player who moved a key was told the
   * shipped one by the ability strip and by both hand cards. The overrides
   * reach *here* (`main.ts`'s `applyBindings` lays the resolved map over this
   * object on every change), which makes the live `Input` the one thing in the
   * program that knows what the controls are. Handing it out is what lets a
   * label be resolved instead of authored.
   *
   * Returned by reference and typed readonly rather than copied: the caller is
   * a per-frame path, and `bindingSerial` is how it avoids doing work when
   * nothing has moved.
   */
  bindingTable(): Readonly<Record<string, Action>> {
    return this.bindings;
  }

  /**
   * Bumped by every `rebind` and `unbind`, so a reader can cache off the table
   * without diffing it. The table is mutated in place, so object identity
   * cannot serve — that is the whole reason this counter exists.
   */
  bindingSerial(): number {
    return this.bindSerial;
  }

  rebind(code: string, action: Action): void {
    this.forgetSource(`k:${code}`);
    this.bindings[code] = action;
    this.bindSerial++;
  }

  /**
   * Stop a key from doing anything.
   *
   * The missing half of `rebind`. On its own `rebind` only ever *adds*: moving
   * jump from Space to Z left Space jumping too, so the settings screen could
   * not offer rebinding without shipping a control that did not do what it
   * said. Removing the key rather than mapping it to a no-op action matters,
   * because `bindings[code]` is also what decides whether a keydown is
   * swallowed at all.
   */
  unbind(code: string): void {
    this.forgetSource(`k:${code}`);
    delete this.bindings[code];
    this.bindSerial++;
  }

  /**
   * The pad twin of `rebind`/`unbind` — issue #191.
   *
   * Same shape, same reason: `pollGamepad` reads `this.padBindings`, not the
   * shipped `DEFAULT_PAD_BINDINGS`, so moving `mainAttack` off button 2 has
   * somewhere to land. `forgetSource(padSource(button))` is the pad half of the
   * latch `rebind`'s keyboard version prevents — rebinding a button that is
   * physically down at the moment of the rebind must not strand its hold under
   * the old action, where nothing still watching that source would ever
   * release it.
   */
  rebindPad(button: number, action: Action): void {
    this.forgetSource(padSource(button));
    this.padBindings[button] = action;
    this.padBindSerial++;
  }

  /** The missing half of `rebindPad`, for the same reason `unbind` exists. */
  unbindPad(button: number): void {
    this.forgetSource(padSource(button));
    delete this.padBindings[button];
    this.padBindSerial++;
  }

  /** The pad twin of `bindingSerial`. */
  padBindingSerial(): number {
    return this.padBindSerial;
  }

  destroy(): void {
    for (const [t, type, fn] of this.listeners) t.removeEventListener(type, fn);
    this.listeners.length = 0;
  }
}
